/**
 * Shared registry for the call module's *file-based* looping sounds.
 *
 * Distinct from `./sound.ts`, which synthesises one-shot oscillator tones via
 * Web Audio (`playCallEnd`, `playChatPing`, …). Those are fire-and-forget and
 * need no lifecycle. Ringback and ringtone are the opposite shape: real media
 * files, looping, started and stopped against a UI lifetime — so they get
 * long-lived <audio> elements rather than per-tone AudioContexts.
 *
 * Assets are served statically from `public/sounds/` (not bundled through
 * `src/assets/`, which would add media to the JS bundle on every page load).
 */

const SOURCES = {
  /** Caller-side "we're trying to reach them" loop. */
  ringback: "/sounds/ringback.mp3",
  /** Callee-side incoming-call loop. */
  ringtone: "/sounds/ringtone.mp3",
} as const;

export type CallSoundName = keyof typeof SOURCES;

const elements = new Map<CallSoundName, HTMLAudioElement>();
const claimed = new Set<CallSoundName>();
let primed = false;

/**
 * The shared <audio> element for a sound, created on first request.
 * One element per sound, reused for the app's lifetime — a call may ring many
 * times per session and re-fetching the file each time would risk starting
 * playback with a network round-trip inside a 45-second ring window.
 */
export function getCallSound(name: CallSoundName): HTMLAudioElement {
  let el = elements.get(name);
  if (!el) {
    el = new Audio(SOURCES[name]);
    el.loop = true;
    el.preload = "auto";
    elements.set(name, el);
  }
  return el;
}

/** Whether {@link primeCallAudio} has already run for this page. */
export function isCallAudioPrimed(): boolean {
  return primed;
}

/**
 * Declare that a live ring owns a sound — GH-1600 (D3).
 *
 * The <audio> elements are shared singletons, so "who is allowed to touch this
 * element right now" cannot be inferred from the element itself: a muted
 * priming playback and a real ringtone are the same object. `useCallSound`
 * claims a sound before it starts playing and releases it on teardown, and
 * {@link primeCallAudio} refuses to act on anything claimed — otherwise its
 * asynchronous cleanup can pause a ringtone the user is actually listening to,
 * which is a missed call with no sound and no explanation.
 *
 * Claiming is *not* mutual exclusion between owners. It marks an element as
 * in-use so the one non-UI writer — priming — stays off it. Arbitration between
 * the two sounds is D7 and arbitration across tabs is D8; both live in
 * `./ringCoordinator.ts`.
 */
export function claimCallSound(name: CallSoundName): void {
  claimed.add(name);
}

/** Release a claim taken by {@link claimCallSound}. */
export function releaseCallSound(name: CallSoundName): void {
  claimed.delete(name);
}

/** Whether a live ring currently owns a sound. */
export function isCallSoundClaimed(name: CallSoundName): boolean {
  return claimed.has(name);
}

/**
 * Fetch + decode both sounds and clear the autoplay gate.
 *
 * Two separate problems, both solved here and both needing a real user gesture:
 *
 * 1. **Permission.** Browsers reject `play()` until the user has interacted with
 *    the page. A muted `play()`/`pause()` pair inside the gesture window marks
 *    the elements as user-activated, so a later unmuted `play()` — triggered by
 *    an arriving invite, not by a click — is allowed.
 * 2. **Latency.** `load()` starts fetching and decoding now, so playback begins
 *    immediately later instead of after a round-trip.
 *
 * Both `play()` calls are issued synchronously (no `await` between them) so they
 * sit inside the activating task. Rejections are expected and harmless on
 * browsers that decline the muted unlock; the `load()` still happened.
 */
export function primeCallAudio(): void {
  if (primed) return;
  primed = true;

  for (const name of Object.keys(SOURCES) as CallSoundName[]) {
    const el = getCallSound(name);
    el.load();
    el.muted = true;
    const attempt = el.play();
    const settle = () => {
      // D3 — `settle` runs asynchronously, and a real call can arrive in the
      // meantime and take this very element over. Pausing, rewinding or
      // re-muting it then would silence a ringtone the user is listening to.
      // Leaving a claimed element exactly as its owner set it up is always
      // correct: the owner already unmutes and rewinds before it plays, and
      // already pauses on teardown.
      if (claimed.has(name)) return;
      el.pause();
      el.currentTime = 0;
      el.muted = false;
    };
    // `play()` returns undefined on very old browsers; guard before chaining.
    if (attempt && typeof attempt.then === "function") {
      // The rejection path settles too. A refused priming playback must still
      // leave the sound unmuted and not playing — unmuting alone would bank a
      // muted-but-playing element on any browser that rejects *after* starting
      // playback, and the next real ring would then be inaudible.
      attempt.then(settle, settle);
    } else {
      settle();
    }
  }
}

/**
 * Prime on the user's first interaction, whatever it happens to be.
 *
 * Called once at module scope from `main.tsx`. Listeners are `capture`d so a
 * component calling `stopPropagation()` cannot swallow the gesture, and all of
 * them are removed as soon as any one fires — `{ once: true }` alone would
 * leave the other event types registered.
 */
export function installCallAudioPrimer(): void {
  if (typeof window === "undefined") return;

  const events = ["pointerdown", "keydown", "touchstart"] as const;

  const onFirstGesture = () => {
    for (const type of events) {
      window.removeEventListener(type, onFirstGesture, true);
    }
    primeCallAudio();
  };

  for (const type of events) {
    window.addEventListener(type, onFirstGesture, { capture: true, passive: true });
  }
}
